/**
 * Words the spell checker accepts on top of the ENABLE word list, plus the ranking aid
 * for its suggestions. All of it was written for this tool; ENABLE itself is a public-domain
 * list of Scrabble-style words and carries no proper nouns, no contractions and very
 * little of the vocabulary that has appeared since the 1990s.
 *
 * Every word below was checked against ENABLE when this file was made and is listed only
 * because ENABLE lacks it. Lookup is case-insensitive, so names are stored in lowercase.
 */

const SPACE = /\s+/;

function words(list: string): string[] {
  return list.trim().split(SPACE);
}

/** Technology and web vocabulary, brand and product names. */
const TECH = `
  app apps backend bitcoin blockchain blog blogger bloggers blogging blogs bluetooth
  chatbot chatbots chatgpt clickbait crossref crowdsourced crowdsourcing
  cryptocurrencies cryptocurrency css cyberbullying cybercrime cybersecurity dataset
  datasets dropbox ebook ebooks email emailed emailing emails emoji emojis emoticons
  facebook frontend geolocation github gmail google googled googling grammarly
  hashtag hashtags homepage homepages html hyperlink hyperlinked hyperlinks inbox
  inboxes influencer influencers instagram internet intranet ios ipad iphone
  javascript jstor linkedin linux livestream livestreamed livestreams login logins
  logout macos meme memes mendeley metadata microsoft netflix newsfeed newsfeeds
  offline ok online openai orcid paywall paywalls pdf pdfs plugin plugins podcast
  podcasts popup popups powerpoint pubmed reddit refworks retweet retweets scopus
  screencast screenshot screenshots scribbr selfie selfies skype smartphone
  smartphones smartwatch spotify subreddit texting tiktok timestamp timestamps
  touchscreen touchscreens unfollow unfriend usb username usernames vlog vlogger
  vlogs webinar webinars webpage webpages website websites whatsapp wifi wiki
  wikipedia wikis youtube zotero
`;

/** Research vocabulary, methods and tests, science and policy terms. */
const ACADEMIC = `
  ancova anova apoptosis asean atp bayesian bioinformatics biomarker biomarkers
  biopsychosocial bonferroni brexit cisgender conceptualization coronavirus
  covariate covariates covid crispr cronbach datafication decolonial
  deindustrialization dna double-blind ecg eeg ekg elisa epigenetics eurozone fmri
  frequentist generalizability genomics globalisation heteronormative
  heteronormativity heteroscedasticity homoscedasticity intercoder interdisciplinary
  interrater intersectionality intrarater lgbtq likert lncrna manova meg
  metaanalyses metaanalysis metabolome metabolomics microbiome microbiomes
  microplastics mri mrna multicollinearity multidisciplinary nanomaterials
  nanoparticles nasa nato neurodivergent neurodiversity neuroimaging neurolinguistic
  neuroplasticity neurotypical opec operationalization operationalize
  operationalized pcr pearson peerreviewed phenomenological postcolonialism
  poststructuralism poststructuralist proteome proteomics psycholinguistic
  psycholinguistics randomised rna sars sirna sociolinguistics spss telehealth
  telemedicine telework transcriptome transcriptomics transdisciplinary transgender
  unesco unicef wilcoxon
`;

/** Abbreviations that appear in scholarly prose and reference lists. */
const ABBREVIATIONS = `
  apa approx arxiv assn bc bce bibtex bsc ca capt ce cf ch cit co corp csl dept doi
  dphil dr eds eg etc ff gov ibid ie ieee inc isbn issn jd jr llb loc lt ltd mba md
  mla mr mrs ms msc mss mt ph phd pm pmcid pmid pp repr ris seq sgt sr st suppl univ
  url urls viz vol vols vs
`;

/** Contractions, with straight apostrophes. */
const CONTRACTIONS = `
  ain't aren't can't could've couldn't didn't doesn't don't hadn't hasn't haven't
  he'd he'll he's here's how's i'd i'll i'm i've isn't it'd it'll it's let's ma'am
  might've mightn't must've mustn't ne'er needn't o'clock shan't she'd she'll she's
  should've shouldn't that'll that's there'd there'll there's they'd they'll they're
  they've wasn't we'd we'll we're we've weren't what'll what're what's what've
  when's where's who'd who'll who's who've why's won't would've wouldn't y'all you'd
  you'll you're you've
`;

/** Countries, regions, states, cities and universities. */
const PLACES = `
  afghanistan africa alabama albania algeria americas amsterdam andorra angola
  antarctica argentina arizona arkansas armenia asia atlanta atlantic auckland
  australia austria azerbaijan bahamas bahrain balkans bangladesh barbados beijing
  belarus belgium belize benin berkeley bhutan birmingham bosnia botswana britain
  brooklyn brunei brussels bulgaria burkina burundi cairo california caltech
  cambodia cambridge cameroon canada cardiff caribbean carolina chicago colombia
  columbia comoros connecticut cornell croatia cuba czechia dakota dallas dartmouth
  delaware delhi denmark djibouti dominica dublin ecuador edinburgh egypt england
  eritrea estonia eswatini ethiopia europe fiji finland florida france gabon georgia
  germany ghana greece grenada guatemala guyana haiti harvard hawaii heidelberg
  honduras houston hungary iceland idaho illinois india indiana indonesia iowa iran
  iraq ireland israel italy jakarta jamaica johannesburg kansas kazakhstan kentucky
  kenya kiribati kosovo kuwait kyrgyzstan lagos laos latvia lebanon lesotho liberia
  libya liechtenstein lithuania liverpool london louisiana luxembourg madagascar
  madrid maine malawi malaysia maldives mali malta manchester maryland massachusetts
  mauritania mauritius melbourne mexico miami michigan micronesia minnesota
  mississippi missouri moldova monaco mongolia montana montenegro montreal
  mozambique mumbai myanmar nairobi namibia nauru nebraska nepal netherlands nevada
  nicaragua niger nigeria norway oceania ohio oklahoma oman oregon ottawa pakistan
  palau palestine paraguay pennsylvania peru philadelphia philippines poland
  portugal princeton qatar romania rome russia rwanda samoa scandinavia scotland
  seattle senegal seoul serbia seychelles siberia singapore slovakia slovenia
  somalia sorbonne spain stanford sudan suriname sweden switzerland sydney syria
  taiwan tajikistan tanzania tennessee thailand togo tokyo toronto tunisia
  turkmenistan tuvalu uganda ukraine uruguay utah uzbekistan vancouver vanuatu
  vatican venezuela vermont vienna vietnam virginia washington wisconsin wyoming
  yale yemen zambia zimbabwe
`;

/** Nationalities, languages, religions and peoples. */
const DEMONYMS = `
  african africans algerian american americans anglo arab arabs argentinian asian
  asians australian australians austrian bangladeshi belgian bengali brazilian
  brazilians british briton britons buddhism buddhist buddhists bulgarian canadian
  canadians cantonese caucasian celtic chilean chinese christian christianity
  christians colombian confucian cuban czech esperanto ethiopian european europeans
  farsi filipino filipinos finnish gaelic ghanaian greeks hebrew hellenic hindi
  hindu hinduism hindus hispanic hungarian icelandic indian indians indonesian inuit
  iranian iraqi irish islam islamic israeli italian italians japanese jewish judaism
  kenyan korean koreans latin latina latinx malaysian maori mexican mexicans
  moroccan muslim muslims nigerian norwegian pakistani palestinian persian peruvian
  portuguese punjabi romanian russian russians saudi scottish sikh sikhism sikhs
  slavic slovak somali spanish swahili swedish tamil taoism thai turkish ukrainian
  urdu venezuelan vietnamese yoruba zulu
`;

/** Surnames of scholars, authors and statisticians that appear in citations. */
const PEOPLE = `
  adler ainsworth angelou archimedes arendt aristotle asch austen avogadro baldwin
  bandura barthes bayes beauvoir beethoven bentham bohr boltzmann bonferroni
  bourdieu bowlby bronte camus cervantes chaucer chekhov chomsky churchill cohen
  copernicus cronbach dante darwin derrida descartes dostoevsky durbin durkheim
  eliot emerson engels erikson euclid euler faulkner fermat festinger feynman
  fibonacci fitzgerald fleming foucault freud friedman galileo gandhi godel goethe
  goffman gramsci habermas halley hawthorne hayek hegel heisenberg hemingway hilbert
  hobbes hubble hume jefferson joyce jung kafka kahneman kant kennedy kepler keynes
  kierkegaard kolmogorov kruskal lacan laplace leonardo levene likert lincoln locke
  machiavelli malthus mandela mann marx maslow melville mendel mendeleev mendelssohn
  merkel michelangelo milgram milton monet montesquieu morrison mozart nietzsche
  nussbaum obama orwell ovid pasteur pavlov pearson piaget picasso piketty planck
  plato poe pythagoras rawls rembrandt ricardo riemann roosevelt russell sartre
  saussure schrodinger schumpeter shakespeare shapiro simmel smirnov socrates
  spinoza steinbeck thoreau tolstoy tukey turing tversky virgil voltaire vygotsky
  wallis washington watson whitman whitney wilcoxon wittgenstein woolf zimbardo
`;

/** About a thousand frequent English words, plus the words people most often misspell. Used only to break ties between suggestions: when two corrections are equally close, the common word is probably the one meant. */
const COMMON = `
  able about above abstract accident accommodate accommodation according account
  achieve acknowledge acquaintance acquire across actually add address afraid after
  afternoon again against aggressive ago agree air airline airport all allow almost
  alone along already also although always am amateur among an analysis and anger
  angry animal annual another answer any apart apparent appear appearance appendix
  apple approach are area argument arm army around arrangement article artist as
  asked at atmosphere attention audience august author authority autumn available
  away awkward baby back bank basically be beach beautiful because bed been before
  began beginning behavior behind belief believe below benefit beside between beyond
  bibliography big bike biology bird black blood blue body book both bottom boy
  bread break bridge bright bring brother brown build bus business busy but buy by
  calendar came camera can car care careful carefully carry case cat catch category
  cause cemetery center central centre century certain certainly chair chance change
  changeable chapter chemistry child choice choose church citation city class clean
  clear climate close cloud coffee cold college color colour column come commitment
  committee common communicate community company comparison competition completely
  computer conclusion conscience conscious consensus consider context continue
  control convenient cool corner correct correlation cost could country couple
  course cover create criticism culture current currently cut dance danger dangerous
  dark data daughter day decade decide decision deep definitely degree dependent
  description desperate develop development dictionary did die difference different
  difficult dilemma direction directly dirty disappear disappoint discipline
  discussion disease dissatisfied distance do doctor does dog door double draw
  driver dry during duty each ear early earth easily easy eat economic economics
  edge education effect effective eight eighth either eleven embarrass emphasize
  empty energy engine engineer english enough environment equipment error especially
  essay even evening every evidence exactly exaggerate exam example excellent except
  exercise existence expect experience explain explanation eye face fact factor
  factory failure fall false familiar family far farm fascinate fast father fear
  federal felt few field fifty figure film final finally fire first fish five flat
  floor flower fluorescent follow following food foot for force foreign forest form
  forty found four fourth free freedom french fresh friend from front fruit full
  function further future game garden general generally gentle geography german get
  girl give go goal good got government grade grammar grass gray great green grey
  group grow guarantee guidance had half hand happen happy harass hard has have he
  head health heart heavy height help her here hierarchy high him his history hit
  home hope horse hospital hot hotel hour house how however human humorous hundred
  husband hypocrisy hypothesis idea if ignorance immediately important in include
  increase independent information inside intelligence interest interesting
  interfere international interrupt into introduction involve irresistible is island
  it its jewelry job joy judgment just justice kept kill kind kitchen know knowledge
  laboratory lake language large last late later law lawyer lead leader learn left
  leg leisure length lesson let letter level library license lie life light
  lightning like likely listen literature little live local long look lose loud love
  lovely machine made main mainly maintenance major make man manager manufacture
  many market marriage match mathematics maybe me measure meat medical medicine meet
  member message method methods middle milk millennium million mind miniature minute
  mischievous mistake model moment money month moon more morning mortgage most
  mostly mother mountain mouth move much museum music my name narrow national
  natural nature near nearly necessary neighbor neither never new news next nice
  night nine no nose not noticeable now number nurse occasion occurrence ocean of
  offer office officer official often old omission on once one only onto open
  opinion opportunity or orange order original other our out outside over own pain
  paper paragraph parallel parent parliament part participants particular
  particularly pass past pastime pattern pay peace people performance perhaps period
  permanent perseverance persistent personal personnel philosophy phone physical
  physics pink place plan plane play player point police policy political politics
  poor popular position possession possible potatoes power practically practice
  preferred prejudice presence present pressure previous price private privilege
  probably problem process produce product professor program progress project
  pronunciation proud provide psychology public publicly pull purple purpose put
  quality quantity question questionnaire quick quickly quiet quite race radio rain
  raise rate rather reach read reader ready real realize really reason receipt
  receive recent recently recognize recommend red reference references referred
  regression relevant religion religious remain remember repetition report
  representative require research restaurant result results return review rhythm
  rich ridiculous right risk river road room rough round rule run sacrifice sad safe
  said salt same sample samples saw say schedule school science scientist scissors
  screen sea season second secretary section see seemed sell send sentence separate
  sergeant serious serve service set seven several shallow sharp she ship shop short
  show side significant similar simple simply since sincerely single sister sit six
  skiing sky slow slowly small smooth snow so social society sociology soft solution
  some sometimes son song soon sophomore space speak special specific specifically
  speed spell spelling spend sponsor sport spring stand standard star start state
  station stay still stone stop store story street strength strong structure student
  study studying subtle succeed success successful such suddenly sugar suggest
  summer sun supersede support sure surprise system table take tall tea teacher team
  technology television temperature ten tendency test text than that the their them
  then theory there therefore these thesis they thick thin think third thirty this
  thorough though thought thousand three through thus ticket time tired to today
  together told tomorrow tongue tonight took tool top toward town trade tragedy
  train tree trend trouble truck true truly truth turn twelve twenty two typically
  under understand understanding unfortunately unique university unless until up
  upon us use useful usually vacuum valuable value variable variables various
  vegetable vehicle very vicious village voice wait walk wall want war warm was
  watch water way we weather week weekend weight weird well went were wet what
  whatever when whenever where whereas wherever whether which whichever while white
  who whoever whole wide width wife will win wind window winter with within without
  woman word words work worker world would write writer writing wrong year yellow
  yesterday yet yield you young your
`;

/** Everything ENABLE lacks that the checker accepts. Lowercase, straight apostrophes. */
export const EXTRA_WORDS: ReadonlySet<string> = new Set([
  ...words(TECH),
  ...words(ACADEMIC),
  ...words(ABBREVIATIONS),
  ...words(CONTRACTIONS),
  ...words(PLACES),
  ...words(DEMONYMS),
  ...words(PEOPLE),
]);

/** Names of places, peoples and scholars: suggested only for a word typed with a capital. */
export const PROPER_WORDS: ReadonlySet<string> = new Set([
  ...words(PLACES),
  ...words(DEMONYMS),
  ...words(PEOPLE),
]);

/** Frequent words, used only to rank suggestions that are otherwise equally close. */
export const COMMON_WORDS: ReadonlySet<string> = new Set(words(COMMON));
